import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  checkIsMergeAndAcquisitionLayout,
  checkIsVentureFormationLayout,
  ENTRY_DATA_SECTION_KEYS,
} from "@frontend/common";
import { DEAL_TYPE_KEYS } from "@frontend/common/src/business/FillInTable/constants/dealTypeKeys.constants";

import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

type CurrentDealType = {
  currentDealTypeList: string[];
  isMergeAndAcquisition: boolean;
  isVentureFormation: boolean;
};

const CheckCurrentDealTypeContext = createContext<CurrentDealType>({
  currentDealTypeList: [],
  isMergeAndAcquisition: false,
  isVentureFormation: false,
});

export const CheckCurrentDealTypeProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const draftArticleId = getPressIdFromURL();
  const [entryData] = useEntryDataState(draftArticleId);
  const [currentChoiceDealType, setCurrentChoiceDealType] = useState<string[]>([
    DEAL_TYPE_KEYS.licenseAgreement,
  ]);
  const articleSection = useArticleSection();

  const currentDealType = useMemo(() => {
    if (
      articleSection !== ENTRY_DATA_SECTION_KEYS.deals &&
      articleSection !== ENTRY_DATA_SECTION_KEYS.dealFiling
    )
      return [];

    return (
      entryData?.[articleSection]?.["Deal Type & Status"]?.[
        "Deal Type"
      ].value.map((dealTypeItem) => dealTypeItem.text) || []
    );
  }, [entryData, articleSection]);

  useEffect(() => {
    const isMAndAType = checkIsMergeAndAcquisitionLayout(currentDealType);

    if (isMAndAType)
      return setCurrentChoiceDealType([DEAL_TYPE_KEYS.mergeAndAcquisition]);

    setCurrentChoiceDealType(
      currentDealType.map((dealType) => dealType.toLocaleLowerCase()),
    );
  }, [currentDealType]);

  const returnCurrentDealType = useMemo(() => {
    return {
      currentDealTypeList: currentChoiceDealType,
      isMergeAndAcquisition: checkIsMergeAndAcquisitionLayout(
        currentChoiceDealType,
      ),
      isVentureFormation: checkIsVentureFormationLayout(currentChoiceDealType),
    };
  }, [currentChoiceDealType]);

  return (
    <CheckCurrentDealTypeContext.Provider value={returnCurrentDealType}>
      {children}
    </CheckCurrentDealTypeContext.Provider>
  );
};

export const useCurrentDealTypeValue = () => {
  const { currentDealTypeList, isMergeAndAcquisition, isVentureFormation } =
    useContext(CheckCurrentDealTypeContext);

  return {
    currentDealTypeList,
    isMergeAndAcquisition,
    isVentureFormation,
  };
};
